import { getController, apiService as Service, type HpRequestPostPut } from '../service'

const url = 'craftsman_tasks'

// AAR-1808 "E-Mail an Beteiligte" — kept local (not in the global Api.*
// namespace) since these types are only consumed by the direct-mail service
// methods and the DirectMailDialog component.
export type CraftsmanTaskDirectEmailRecipientKind =
	| 'craftsman_task_person'
	| 'contact_person'
	| 'craftsman_person'
	| 'free'

export type CraftsmanTaskDirectEmailRecipient = {
	kind: CraftsmanTaskDirectEmailRecipientKind
	// task-person link id (craftsman_task_person) or task-person-contact link
	// id (contact_person). Absent for craftsman_person (resolved from the
	// task's craftsman_person_id) and for free (email only).
	link_id?: number
	email?: string
}

export type CraftsmanTaskDirectEmailPreviewInput = {
	mailtemplate_id: number
	to?: CraftsmanTaskDirectEmailRecipient[]
}

export type CraftsmanTaskDirectEmailPreviewResponse = {
	subject: string
	body: string
	missing_placeholders: string[]
}

export type CraftsmanTaskDirectEmailPostInput = {
	subject: string
	body: string
	to: CraftsmanTaskDirectEmailRecipient[]
	cc?: CraftsmanTaskDirectEmailRecipient[]
	bcc?: CraftsmanTaskDirectEmailRecipient[]
	// AAR-1813 "Anhaenge" — files are newly uploaded (become a fresh, internal
	// document_container attached to BOTH the task's Dokumente tab and the
	// email); document_container_ids reference containers already hanging on
	// the task (attached to the email only, see backend controller docblock).
	files?: globalThis.File[]
	document_container_ids?: number[]
}

export const craftsmanTaskService = {
	...getController<
		Api.CraftsmanTask,
		Api.CraftsmanTaskPostInput,
		Api.CraftsmanTaskUpdateInput
	>(url),
	postComment: ({
		module_id,
		history_id,
		data,
	}: {
		module_id: number
		history_id: number
		data: HpRequestPostPut<unknown>
	}) =>
		Service.post({
			url: `${url}/${module_id}/history/${history_id}/comments`,
			...data,
		}),
	updateComment: ({
		module_id,
		history_id,
		comment_id,
		data,
	}: {
		module_id: number
		history_id: number
		comment_id: number
		data: HpRequestPostPut<unknown>
	}) =>
		Service.put({
			url: `${url}/${module_id}/history/${history_id}/comments/${comment_id}`,
			...data,
		}),
	removeComment: ({
		module_id,
		history_id,
		comment_id,
	}: Record<string, number>) =>
		Service.delete({
			url: `${url}/${module_id}/history/${history_id}/comments/${comment_id}`,
		}),
	previewDirectEmail: (
		id: number,
		data: HpRequestPostPut<CraftsmanTaskDirectEmailPreviewInput>
	) =>
		Service.post<{ data: CraftsmanTaskDirectEmailPreviewResponse }>({
			url: `${url}/${id}/direct_email/preview`,
			...data,
		}),
	directEmail: (
		id: number,
		data: HpRequestPostPut<CraftsmanTaskDirectEmailPostInput>
	) => {
		// AAR-1813 — mirrors ReportCreateComment.vue's commentAndMail(): a plain
		// object with an explicit multipart Content-Type header lets axios's
		// default transformRequest build the FormData itself (nested to/cc/bcc
		// arrays become bracket-notation fields, e.g. to[0][kind], which Laravel
		// parses back into a real array — see StoreCraftsmanTaskDirectEmailRequest).
		// Only switch to multipart when there is actually a file to carry —
		// document_container_ids alone is plain JSON-serializable data.
		const hasFiles = (data.data.files?.length ?? 0) > 0
		return Service.post<unknown>({
			url: `${url}/${id}/direct_email`,
			...data,
			...(hasFiles
				? { headers: { ...data.headers, 'Content-Type': 'multipart/form-data' } }
				: {}),
		})
	},
}

export default craftsmanTaskService
